import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WorkspaceDetail } from '@seventide/types';
import { api, wsPath } from '../lib/api';
import { qk } from '../lib/queryClient';

export interface WorkspaceContextValue {
  wsId: string;
  workspace: WorkspaceDetail;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/** GET /api/w/:wsId → WorkspaceDetail. Prefer `useWorkspace()` inside the workspace shell. */
export function useWorkspaceQuery(wsId: string | undefined) {
  return useQuery({
    queryKey: qk.workspace(wsId ?? ''),
    queryFn: () => api.get<WorkspaceDetail>(wsPath(wsId ?? '')),
    enabled: Boolean(wsId),
  });
}

/**
 * Current workspace (from the `:wsId` route, loaded by `<RequireWorkspace>`). Always defined inside
 * `/w/:wsId/*` routes; throws elsewhere so mistakes show up immediately.
 */
export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace() must be used inside a /w/:wsId route');
  return ctx;
}

/** Same as useWorkspace() but returns null outside a workspace (for shared components). */
export function useOptionalWorkspace(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext);
}
